import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Button,
    IconButton,
    Tooltip,
    Divider,
    Chip,
    CircularProgress,
    LinearProgress,
    Fade,
    Collapse,
} from '@mui/material';
import { useQuery } from 'react-query';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip as ChartTooltip,
    Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

import { getStatsHighlights, getDailyStats } from 'src/services/StatsService';
import { StatsHighlights, DailyStats } from 'src/types/Stats';
import useIsMobileSize from 'src/hooks/useIsMobileSize';
import TitleComponent from 'src/components/Title';

// Регистрируем компоненты Chart.js
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, ChartTooltip, Legend);

interface StatsBlockProps {
    isExpanded?: boolean;
    onToggleExpanded?: () => void;
}

const StatsBlock: React.FC<StatsBlockProps> = ({ isExpanded = false, onToggleExpanded }) => {
    const { enqueueSnackbar } = useSnackbar();
    const isMobile = useIsMobileSize();
    const [dateFrom, setDateFrom] = useState(dayjs().subtract(1, 'week').format('YYYY-MM-DD'));
    const [dateTo, setDateTo] = useState(dayjs().format('YYYY-MM-DD'));
    const [hasInitialData, setHasInitialData] = useState(false);
    const [chartReady, setChartReady] = useState(false);

    // Получение основных статистик
    const { data: highlightsResponse, isLoading: isLoadingHighlights } = useQuery({
        queryKey: ['stats-highlights'],
        queryFn: getStatsHighlights,
        onError: () => {
            enqueueSnackbar('Ошибка получения статистики', { variant: 'error' });
        },
    });

    // Получение ежедневной статистики (все сообщения)
    const { data: dailyStatsResponse, isLoading: isLoadingDaily } = useQuery({
        queryKey: ['daily-stats', dateFrom, dateTo, false],
        queryFn: () => getDailyStats({ from: dateFrom, to: dateTo, relevant_only: false }),
        onError: () => {
            enqueueSnackbar('Ошибка получения ежедневной статистики', { variant: 'error' });
        },
        onSuccess: (data) => {
            if (!hasInitialData && data?.data?.data?.length > 0) {
                setHasInitialData(true);
            }
        },
    });

    // Получение ежедневной статистики (только релевантные + лиды)
    const { data: dailyRelevantStatsResponse, isLoading: isLoadingRelevantDaily } = useQuery({
        queryKey: ['daily-stats', dateFrom, dateTo, true],
        queryFn: () => getDailyStats({ from: dateFrom, to: dateTo, relevant_only: true }),
        onError: () => {
            enqueueSnackbar('Ошибка получения статистики релевантных сообщений', { variant: 'error' });
        },
    });

    // Извлекаем данные из axios response
    const highlights = highlightsResponse?.data;
    const dailyStats = dailyStatsResponse?.data;
    const dailyRelevantStats = dailyRelevantStatsResponse?.data;
    const leadSummary = dailyRelevantStats?.lead_summary;

    // Если нет данных за последнюю неделю, попробуем получить с более ранней даты
    useEffect(() => {
        if (
            !hasInitialData &&
            !isLoadingDaily &&
            dailyStats?.data?.length === 0 &&
            dateFrom === dayjs().subtract(1, 'week').format('YYYY-MM-DD')
        ) {
            setDateFrom(dayjs().startOf('year').format('YYYY-MM-DD'));
        }
    }, [dailyStats, isLoadingDaily, hasInitialData, dateFrom]);

    const formatChartData = (data: DailyStats[]) => {
        return (
            data?.map((item) => ({
                date: dayjs(item.date).format('DD.MM'),
                count: Number(item.count) || 0,
            })) || []
        );
    };

    // Данные для графика из API
    const formattedTotalData = formatChartData(dailyStats?.data || []);
    const formattedRelevantData = formatChartData(dailyRelevantStats?.data || []);

    // Создаем полный диапазон дат от dateFrom до dateTo
    const startDate = new Date(dateFrom);
    const endDate = new Date(dateTo);
    const allDates: string[] = [];

    const currentDate = new Date(startDate);
    while (currentDate <= endDate) {
        allDates.push(dayjs(currentDate).format('DD.MM'));
        currentDate.setDate(currentDate.getDate() + 1);
    }

    const chartData = {
        labels: allDates,
        datasets: [
            {
                label: 'Всего сообщений',
                data: allDates.map((date) => {
                    const item = formattedTotalData.find((d) => d.date === date);
                    return item ? item.count : 0;
                }),
                backgroundColor: '#9FA9BC',
                borderColor: '#9FA9BC',
                borderWidth: 1,
            },
            {
                label: 'Целевые сообщения',
                data: allDates.map((date) => {
                    const item = formattedRelevantData.find((d) => d.date === date);
                    return item ? item.count : 0;
                }),
                backgroundColor: '#0044FF',
                borderColor: '#0044FF',
                borderWidth: 1,
            },
        ],
    };

    const chartColors = ['#9FA9BC', '#0044FF'];

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top' as const,
                labels: {
                    usePointStyle: true,
                    padding: 20,
                    font: { size: 12, weight: 500 },
                    generateLabels: (chart) => {
                        const datasets = chart.data.datasets;
                        return datasets.map((dataset, index) => ({
                            text: dataset.label || '',
                            fillStyle: chartColors[index] || '#9FA9BC',
                            strokeStyle: chartColors[index] || '#9FA9BC',
                            lineWidth: 0,
                            pointStyle: 'circle',
                            hidden: false,
                            index: index,
                        }));
                    },
                },
            },
            tooltip: {
                backgroundColor: 'rgba(0,0,0,0.8)',
                titleColor: '#fff',
                bodyColor: '#fff',
                borderColor: 'rgba(255,255,255,0.2)',
                borderWidth: 1,
                cornerRadius: 8,
                displayColors: true,
                callbacks: {
                    labelColor: (context) => {
                        return {
                            borderColor: chartColors[context.datasetIndex] || '#fff',
                            backgroundColor: chartColors[context.datasetIndex] || '#fff',
                        };
                    },
                },
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                grid: {
                    color: 'rgba(0,0,0,0.1)',
                    drawBorder: false,
                },
                ticks: {
                    font: { size: 11 },
                },
            },
            x: {
                grid: {
                    color: 'rgba(0,0,0,0.1)',
                    drawBorder: false,
                },
                ticks: {
                    font: { size: 11 },
                },
            },
        },
        interaction: {
            intersect: false,
            mode: 'index' as const,
        },
    };

    // Отладочная информация (можно убрать после тестирования)
    // console.log('StatsBlock Debug:', { highlights, dailyStats, dailyRelevantStats, allDates });

    if (isLoadingHighlights && isLoadingDaily && isLoadingRelevantDaily) {
        return (
            <Paper
                elevation={0}
                sx={{ p: 3, mb: 3, borderRadius: 2, background: '#fff', boxShadow: 'none !important' }}
            >
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    py={4}
                >
                    <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        gap={2}
                    >
                        <CircularProgress size={40} />
                        <Typography
                            color="text.primary"
                            sx={{ fontWeight: 400 }}
                        >
                            Загрузка статистики...
                        </Typography>
                    </Box>
                </Box>
            </Paper>
        );
    }

    // Всегда показываем блок статистики, даже если данных нет

    return (
        <Box>
            {/* Объединенный блок метрик и графика */}
            <Paper
                elevation={0}
                sx={{ p: 2, mb: 3, borderRadius: 2, background: '#fff', boxShadow: 'none !important' }}
            >
                {/* Заголовок с кнопкой подробнее */}
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                >
                    <Typography
                        variant="h5"
                        fontWeight={700}
                        color="primary.main"
                    >
                        Аналитика
                    </Typography>
                    {onToggleExpanded && (
                        <Box
                            display="flex"
                            alignItems="center"
                            gap={1}
                        >
                            <span style={{ fontSize: '1.2rem' }}>📊</span>
                            <Button
                                variant="text"
                                color="primary"
                                onClick={onToggleExpanded}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    fontSize: '0.875rem',
                                }}
                            >
                                {isExpanded ? 'Свернуть' : 'Подробнее'}
                            </Button>
                        </Box>
                    )}
                </Box>

                {/* Основные метрики */}
                <Grid
                    container
                    spacing={2}
                    mb={2}
                >
                    <Grid
                        item
                        xs={12}
                        sm={6}
                    >
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                bgcolor: '#EFF3F7',
                                textAlign: 'center',
                                borderRadius: 2,
                                border: '1px solid rgba(0,0,0,0.05)',
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                },
                            }}
                        >
                            <Typography
                                variant="h5"
                                color="primary.main"
                                fontWeight={700}
                            >
                                {highlights?.found_messages ?? 0}
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.primary"
                                sx={{ fontWeight: 400, fontSize: '0.875rem' }}
                            >
                                Всего сообщений
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        sm={6}
                    >
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                bgcolor: '#EFF3F7',
                                textAlign: 'center',
                                borderRadius: 2,
                                border: '1px solid rgba(0,0,0,0.05)',
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                },
                            }}
                        >
                            <Typography
                                variant="h5"
                                color="#0044FF"
                                fontWeight={700}
                            >
                                {highlights?.relevant_messages ?? 0}
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.primary"
                                sx={{ fontWeight: 400, fontSize: '0.875rem' }}
                            >
                                Целевых сообщений
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>

                {leadSummary && (
                    <Box
                        display="flex"
                        flexWrap="wrap"
                        gap={isMobile ? 1 : 2.5}
                        mb={2}
                        px={1}
                    >
                        <Typography variant="body2">Лиды: {leadSummary.unique_leads}</Typography>
                        <Typography variant="body2">
                            Отправки в Telegram: {leadSummary.telegram_recipient_sends}
                        </Typography>
                        <Typography variant="body2">Дубли отсеяны: {leadSummary.duplicates_filtered}</Typography>
                        <Typography variant="body2">Возвраты: {leadSummary.refunded_credits}</Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                        >
                            Свежесть в среднем:{' '}
                            {leadSummary.freshness.avg_source_age_minutes === null
                                ? '—'
                                : `${leadSummary.freshness.avg_source_age_minutes} мин`}
                        </Typography>
                    </Box>
                )}

                {/* График с данными из API */}
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={1.5}
                >
                    <Box
                        display="flex"
                        gap={1}
                    >
                        <Chip
                            label="Сегодня"
                            onClick={() => {
                                setDateFrom(dayjs().format('YYYY-MM-DD'));
                                setDateTo(dayjs().format('YYYY-MM-DD'));
                            }}
                            variant="outlined"
                            size="small"
                        />
                        <Chip
                            label="Неделя"
                            onClick={() => {
                                setDateFrom(dayjs().subtract(1, 'week').format('YYYY-MM-DD'));
                                setDateTo(dayjs().format('YYYY-MM-DD'));
                            }}
                            variant="outlined"
                            size="small"
                        />
                        <Chip
                            label="Месяц"
                            onClick={() => {
                                setDateFrom(dayjs().subtract(1, 'month').format('YYYY-MM-DD'));
                                setDateTo(dayjs().format('YYYY-MM-DD'));
                            }}
                            variant="outlined"
                            size="small"
                        />
                    </Box>
                    <Box
                        display="flex"
                        gap={1}
                        alignItems="center"
                    >
                        <TextField
                            type="date"
                            value={dateFrom}
                            onChange={(e) => setDateFrom(e.target.value)}
                            size="small"
                            sx={{
                                minWidth: 'auto',
                                '& .MuiInputBase-root': {
                                    height: 32,
                                    fontSize: '0.875rem',
                                    borderRadius: 2,
                                    '& fieldset': {
                                        borderRadius: 2,
                                    },
                                },
                                '& input[type="date"]::-webkit-calendar-picker-indicator': {
                                    borderRadius: 2,
                                },
                                '& input[type="date"]::-webkit-datetime-edit': {
                                    borderRadius: 2,
                                },
                            }}
                        />
                        <Typography
                            variant="body2"
                            color="text.primary"
                            sx={{ fontWeight: 500 }}
                        >
                            —
                        </Typography>
                        <TextField
                            type="date"
                            value={dateTo}
                            onChange={(e) => setDateTo(e.target.value)}
                            size="small"
                            sx={{
                                minWidth: 'auto',
                                '& .MuiInputBase-root': {
                                    height: 32,
                                    fontSize: '0.875rem',
                                    borderRadius: 2,
                                    '& fieldset': {
                                        borderRadius: 2,
                                    },
                                },
                                '& input[type="date"]::-webkit-calendar-picker-indicator': {
                                    borderRadius: 2,
                                },
                                '& input[type="date"]::-webkit-datetime-edit': {
                                    borderRadius: 2,
                                },
                            }}
                        />
                    </Box>
                </Box>
                {isLoadingDaily || isLoadingRelevantDaily ? (
                    <Box
                        height={250}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        sx={{ bgcolor: '#EFF3F7', borderRadius: 2 }}
                    >
                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            gap={2}
                        >
                            <CircularProgress size={40} />
                            <Typography
                                color="text.primary"
                                sx={{ fontWeight: 400 }}
                            >
                                Загрузка данных...
                            </Typography>
                        </Box>
                    </Box>
                ) : allDates.length > 0 && chartData.labels.length > 0 ? (
                    <Fade
                        in={true}
                        timeout={300}
                    >
                        <Box
                            height={250}
                            position="relative"
                            sx={{ bgcolor: '#EFF3F7', borderRadius: 2, p: 2 }}
                        >
                            <Bar
                                data={chartData}
                                options={chartOptions}
                            />
                        </Box>
                    </Fade>
                ) : (
                    <Box
                        height={250}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        sx={{ bgcolor: '#EFF3F7', borderRadius: 2 }}
                    >
                        <Typography
                            color="text.primary"
                            sx={{ fontWeight: 400 }}
                        >
                            Нет данных для отображения графика
                        </Typography>
                    </Box>
                )}
            </Paper>

            {/* Таблица ключевых слов с анимацией */}
            <Collapse in={isExpanded}>
                {highlights?.top_keywords && highlights.top_keywords.length > 0 ? (
                    <Paper
                        elevation={0}
                        sx={{ p: 3, mb: 3, borderRadius: 2, background: '#fff', boxShadow: 'none !important' }}
                    >
                        <Typography
                            variant="h5"
                            mb={2}
                            fontWeight={700}
                            color="primary.main"
                        >
                            Топ ключевых слов
                        </Typography>
                        <TableContainer
                            component={Paper}
                            elevation={0}
                            sx={{ bgcolor: '#EFF3F7', borderRadius: 2 }}
                        >
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Ключевое слово</TableCell>
                                        <TableCell align="right">Количество</TableCell>
                                        <TableCell align="right">Доля от всех сообщений</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {highlights.top_keywords.map((keyword, index) => {
                                        const percentageFromTotal = highlights?.found_messages
                                            ? (keyword.count / highlights.found_messages) * 100
                                            : 0;
                                        return (
                                            <TableRow key={index}>
                                                <TableCell>{keyword.keyword}</TableCell>
                                                <TableCell align="right">{keyword.count}</TableCell>
                                                <TableCell align="right">
                                                    <Box
                                                        display="flex"
                                                        alignItems="center"
                                                        gap={1}
                                                        sx={{ minWidth: 120 }}
                                                    >
                                                        <Box flex={1}>
                                                            <LinearProgress
                                                                variant="determinate"
                                                                value={percentageFromTotal}
                                                                sx={{
                                                                    height: 6,
                                                                    borderRadius: 3,
                                                                    backgroundColor: 'rgba(0,0,0,0.1)',
                                                                    '& .MuiLinearProgress-bar': {
                                                                        backgroundColor: '#0044FF',
                                                                    },
                                                                }}
                                                            />
                                                        </Box>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{ minWidth: 35, fontSize: 12 }}
                                                        >
                                                            {percentageFromTotal.toFixed(1)}%
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                ) : (
                    <Paper
                        elevation={0}
                        sx={{ p: 3, mb: 3, borderRadius: 2, background: '#fff', boxShadow: 'none !important' }}
                    >
                        <Typography
                            variant="h5"
                            mb={2}
                            fontWeight={700}
                            color="primary.main"
                        >
                            Топ ключевых слов
                        </Typography>
                        <Box
                            textAlign="center"
                            py={4}
                        >
                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                Нет данных о ключевых словах
                            </Typography>
                        </Box>
                    </Paper>
                )}
            </Collapse>
        </Box>
    );
};

export default StatsBlock;
